/**
 * This tab's own realtime connection id, readable without importing the SignalR service.
 *
 * ## Why it is a module of its own
 *
 * Two things need it and they must not import each other. The API client attaches it to every
 * request so the server can name the origin of a change; the SignalR service is what knows the
 * value. The SignalR service already reaches for auth state that the API client owns, so having the
 * client import it back would close a cycle — and a cycle here fails at module-evaluation time, as
 * an undefined export rather than an error that says what happened.
 *
 * A three-line box that neither of them owns costs nothing and cannot form one.
 *
 * ## What the value is for
 *
 * Only so a device can recognise the echo of its own change. When settings are saved, the server
 * tells every device signed in as that user, including the one that just saved — which already has
 * the new values, and would otherwise reload a form under the hand that just submitted it.
 *
 * It is not identity and nothing is authorised by it. The server never reads it for anything but
 * that comparison, so a wrong value costs one skipped refresh on one of your own devices.
 */

let connectionId: string | null = null

/** Called by the SignalR service on connect, reconnect (the id changes), and disconnect. */
export function setRealtimeConnectionId(id: string | null) {
  connectionId = id
}

/** Null before the hub connects, and between a drop and its reconnect. */
export function getRealtimeConnectionId(): string | null {
  return connectionId
}

/**
 * The header the API client sends it under.
 *
 * Shared with the server's own constant by convention rather than by contract — if it is renamed
 * on one side, echo suppression silently stops and every device refreshes once too often. That is
 * the whole failure, which is why this is not worth more machinery than a constant.
 */
export const CONNECTION_ID_HEADER = 'X-Client-Connection-Id'
